import { getOrdersByLocation, getOrdersByOrder } from 'api/orders/api';
import {
  OrdersByLocationSearchParams,
  OrdersByLocationType,
  OrderItemType,
  OrdersExtraMetadataType,
} from 'api/orders/schemas';
import Filters from 'components/Filters';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import Searchbar from 'components/Table/SearchBar';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { useSortedTable } from 'hooks/useSortedTable';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import LocationTable from './LocationTable';
import OrderTable from './OrderTable';
import { SingleValue } from 'react-select';
import { Chip } from 'components/Chip/Chip';
import { getExportOrdersAsCVS } from 'api/orders/api';

export type OrderType = 'all' | 'in_progress' | 'completed';
export type ViewByType = 'location' | 'order';

interface OrdersTableProps {
  activeTab: OrderType;
  viewByTab: ViewByType;
  graphStatus: string | null;
}

const OrdersTable: FC<OrdersTableProps> = ({
  activeTab,
  viewByTab,
  graphStatus,
}) => {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] =
    useState<SingleValue<OptionType>>(null);
  const [filterSiteName, setFilterSiteName] =
    useState<SingleValue<OptionType>>(null);
  const [filterServiceType, setFilterServiceType] =
    useState<SingleValue<OptionType>>(null);

  const clearAllFilters = useCallback(() => {
    setSearch('');
    if (graphStatus === null) setFilterStatus(null);
    setFilterSiteName(null);
    setFilterServiceType(null);
  }, [graphStatus]);

  useEffect(() => {
    if (graphStatus)
      setFilterStatus({ value: graphStatus, label: graphStatus });
  }, [graphStatus]);

  useEffect(() => {
    clearAllFilters();
  }, [viewByTab, activeTab, clearAllFilters]);

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [],
  });

  const getOrdersIndexFn = useCallback(
    (args?: OrdersByLocationSearchParams) => {
      if (viewByTab === 'order') {
        return getOrdersByOrder({
          ...(search ? { search } : {}),
          ...sortingQueryParams,
          tab: activeTab,
          filter_status: filterStatus?.value,
          filter_site_name: filterSiteName?.value,
          filter_service_type: filterServiceType?.value,
          ...args,
        });
      }
      return getOrdersByLocation({
        ...(search ? { search } : {}),
        ...sortingQueryParams,
        tab: activeTab,
        filter_status: filterStatus?.value,
        filter_site_name: filterSiteName?.value,
        filter_service_type: filterServiceType?.value,
        ...args,
      });
    },
    [
      search,
      sortingQueryParams,
      activeTab,
      filterStatus,
      viewByTab,
      filterSiteName,
      filterServiceType,
    ],
  );

  const {
    data: tablePageData,
    metadata,
    ...paginatedTableProps
  } = usePaginatedTable<
    OrderItemType | OrdersByLocationType,
    OrdersExtraMetadataType
  >(
    getOrdersIndexFn,
    {
      viewByTab,
      filterStatus,
      filterSiteName,
      filterServiceType,
      activeTab,
      search,
      sortingQueryParams,
    },
    [
      'orders-tracking-table',
      sortingQueryParams,
      filterSiteName,
      search,
      viewByTab,
      activeTab,
      filterStatus,
      filterServiceType,
    ],
    {
      refetchOnMount: true,
    },
  );

  const siteNamesOptions = useMemo(() => {
    return metadata?.site_names.map((sN) => ({ value: sN, label: sN }));
  }, [metadata]);

  const servicesTypeOptions = useMemo(() => {
    return metadata?.service_types.map((sT) => ({ value: sT, label: sT }));
  }, [metadata]);

  const statusOptions = useMemo(() => {
    return metadata?.statuses?.map((s) => ({ value: s, label: s })) ?? [];
  }, [metadata]);

  const exportOrderCVSFile = async () => {
    const response = await getExportOrdersAsCVS({
      ...(search ? { search } : {}),
      ...sortingQueryParams,
      tab: activeTab,
      filter_status: filterStatus?.value,
      filter_site_name: filterSiteName?.value,
      filter_service_type: filterServiceType?.value,
    });

    const blob = new Blob([response], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'orders.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <div className="grid w-full sm:flex-1">
          <Searchbar
            placeholder="Search by address, site name, or order #"
            onSearch={(query) => {
              setSearch(query);
            }}
            onQueryClear={() => {
              setSearch('');
            }}
            clearAllValues={search === ''}
            defaultQuery={search}
            variant="surface-2"
          />
        </div>
        <Filters
          showTitle={false}
          clearFilters={clearAllFilters}
          clearFilterClassName="col-span-full md:col-span-1"
          className="grid w-full grid-cols-4 gap-4 md:flex"
        >
          <GraniteSelect
            className="col-span-full md:col-span-1"
            placeholder="Order status"
            options={statusOptions}
            controlShouldRenderValue={false}
            isSearchable={false}
            value={filterStatus}
            onChange={setFilterStatus}
            variant="surface-2"
          />
          <GraniteSelect
            options={siteNamesOptions}
            isClearable={false}
            isSearchable={false}
            className="col-span-full md:col-span-1"
            placeholder="Site name"
            controlShouldRenderValue={false}
            value={filterSiteName}
            onChange={setFilterSiteName}
            variant="surface-2"
          />
          <GraniteSelect
            options={servicesTypeOptions}
            isClearable={false}
            isSearchable={false}
            className="col-span-full md:col-span-1"
            placeholder="Service type"
            controlShouldRenderValue={false}
            value={filterServiceType}
            onChange={setFilterServiceType}
            variant="surface-2"
          />
        </Filters>
      </div>
      <div className="flex flex-wrap gap-4">
        {filterStatus?.value && (
          <div className="mt-4 flex">
            <Chip
              label={filterStatus.label}
              onDelete={() => setFilterStatus(null)}
            />
          </div>
        )}
        {filterSiteName?.value && (
          <div className="mt-4 flex">
            <Chip
              label={filterSiteName.label}
              onDelete={() => setFilterSiteName(null)}
            />
          </div>
        )}
        {filterServiceType?.value && (
          <div className="mt-4 flex">
            <Chip
              label={filterServiceType.label}
              onDelete={() => setFilterServiceType(null)}
            />
          </div>
        )}
      </div>
      <div className="mt-8 h-full">
        {viewByTab === 'location' && (
          <LocationTable
            tablePageData={tablePageData as OrdersByLocationType[]}
            sortingState={sortingState}
            onSortingChange={onSortingChange}
            paginatedTableProps={paginatedTableProps}
          />
        )}
        {viewByTab === 'order' && (
          <OrderTable
            tablePageData={tablePageData as OrderItemType[]}
            sortingState={sortingState}
            onSortingChange={onSortingChange}
            paginatedTableProps={paginatedTableProps}
            downloadTableFn={exportOrderCVSFile}
          />
        )}
      </div>
    </>
  );
};

export default OrdersTable;
